import { useState } from "react";
import { Trash } from "lucide-react";
import { Button } from "../../button";
import { Input } from "../../input";
import { Label } from "../../label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select";

export interface EligibilitySubmissionContentProps {
  onSubmit: (formData: {
    testSubmissionType: string;
    provider: string;
    insuranceType: string;
    diagnosisCodes: string;
    currentlyTaking: string;
    previouslyTaken: string;
    selectedDrugs: Array<{
      name: string;
      dosage: string;
      quantity: string;
    }>;
  }) => void;
}

export function EligibilitySubmissionContent({ onSubmit }: EligibilitySubmissionContentProps) {
  const [selectedDrugs, setSelectedDrugs] = useState<
    Array<{
      id: string;
      name: string;
      dosage: string;
      quantity: string;
    }>
  >([]);

  const [newDrug, setNewDrug] = useState({
    name: "",
    dosage: "",
    quantity: "",
  });

  // Form state for submission fields
  const [formData, setFormData] = useState({
    testSubmissionType: "",
    provider: "",
    insuranceType: "",
    diagnosisCodes: "",
    currentlyTaking: "",
    previouslyTaken: "",
  });

  const addDrug = () => {
    if (newDrug.name && newDrug.dosage && newDrug.quantity) {
      setSelectedDrugs([
        ...selectedDrugs,
        {
          id: Date.now().toString(),
          ...newDrug,
        },
      ]);
      setNewDrug({ name: "", dosage: "", quantity: "" });
    }
  };

  const removeDrug = (id: string) => {
    setSelectedDrugs(selectedDrugs.filter((drug) => drug.id !== id));
  };

  const handleSubmit = () => {
    // Submit the form with current data
    onSubmit({
      ...formData,
      selectedDrugs: selectedDrugs.map(({ id: _, ...drug }) => drug), // Remove id for submission
    });
  };

  return (
    <div className="flex-1 overflow-y-auto space-y-6 p-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 p-1">
          <Label htmlFor="test-submission-type">Test Submission Type</Label>
          <Select
            value={formData.testSubmissionType}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, testSubmissionType: value }))}
          >
            <SelectTrigger id="test-submission-type" className="w-full">
              <SelectValue placeholder="Select submission type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="prior-authorization">Prior Authorization</SelectItem>
              <SelectItem value="eligibility-check">Eligibility Check</SelectItem>
              <SelectItem value="benefits-verification">Benefits Verification</SelectItem>
              <SelectItem value="formulary-check">Formulary Check</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="provider">Provider</Label>
          <Select
            value={formData.provider}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, provider: value }))}
          >
            <SelectTrigger id="provider" className="w-full">
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Dr. Sarah Smith">Dr. Sarah Smith</SelectItem>
              <SelectItem value="Dr. Michael Johnson">Dr. Michael Johnson</SelectItem>
              <SelectItem value="Dr. Jennifer Wilson">Dr. Jennifer Wilson</SelectItem>
              <SelectItem value="Dr. Emily Chen">Dr. Emily Chen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="insurance-type">Insurance Type</Label>
          <Select
            value={formData.insuranceType}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, insuranceType: value }))}
          >
            <SelectTrigger id="insurance-type" className="w-full">
              <SelectValue placeholder="Select insurance type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="medicare">Medicare</SelectItem>
              <SelectItem value="medicaid">Medicaid</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="tricare">Tricare</SelectItem>
              <SelectItem value="uninsured">Uninsured</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="diagnosis-codes">Diagnosis Codes</Label>
          <Select
            value={formData.diagnosisCodes}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, diagnosisCodes: value }))}
          >
            <SelectTrigger id="diagnosis-codes" className="w-full">
              <SelectValue placeholder="Select diagnosis codes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="e11.9">E11.9 - Type 2 diabetes mellitus</SelectItem>
              <SelectItem value="i10">I10 - Essential hypertension</SelectItem>
              <SelectItem value="z79.4">Z79.4 - Long term use of insulin</SelectItem>
              <SelectItem value="e78.5">E78.5 - Hyperlipidemia</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="currently-taking">Currently Taking Drugs</Label>
          <Select
            value={formData.currentlyTaking}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, currentlyTaking: value }))}
          >
            <SelectTrigger id="currently-taking" className="w-full">
              <SelectValue placeholder="Select current medications" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="metformin">Metformin</SelectItem>
              <SelectItem value="lisinopril">Lisinopril</SelectItem>
              <SelectItem value="atorvastatin">Atorvastatin</SelectItem>
              <SelectItem value="omeprazole">Omeprazole</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="previously-taken">Previously Taken Drugs</Label>
          <Select
            value={formData.previouslyTaken}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, previouslyTaken: value }))}
          >
            <SelectTrigger id="previously-taken" className="w-full">
              <SelectValue placeholder="Select previous medications" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="insulin">Insulin</SelectItem>
              <SelectItem value="glyburide">Glyburide</SelectItem>
              <SelectItem value="hydrochlorothiazide">Hydrochlorothiazide</SelectItem>
              <SelectItem value="simvastatin">Simvastatin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Drug Selection Section */}
      <div className="space-y-4 border-t pt-6">
        <h3 className="text-lg font-medium">Add Drugs to Check</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label
              htmlFor="drug-name"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Drug Name
            </Label>
            <Input
              id="drug-name"
              placeholder="Enter drug name"
              value={newDrug.name}
              onChange={(e) => setNewDrug({ ...newDrug, name: e.target.value })}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="dosage"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Dosage
            </Label>
            <Input
              id="dosage"
              placeholder="e.g. 500mg"
              value={newDrug.dosage}
              onChange={(e) => setNewDrug({ ...newDrug, dosage: e.target.value })}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="quantity"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Quantity
            </Label>
            <div className="flex gap-2">
              <Input
                id="quantity"
                placeholder="e.g. 30 tablets"
                value={newDrug.quantity}
                onChange={(e) => setNewDrug({ ...newDrug, quantity: e.target.value })}
                className="h-10"
              />
              <Button onClick={addDrug} variant="outline" className="h-10 whitespace-nowrap">
                Add Drug
              </Button>
            </div>
          </div>
        </div>

        {/* Selected Drugs List */}
        {selectedDrugs.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">Selected Drugs:</h4>
            {selectedDrugs.map((drug) => (
              <div key={drug.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{drug.name}</div>
                  <div className="flex items-center">
                    <span className="text-sm text-foreground pr-2">{drug.dosage}</span>
                    <span className="text-sm text-muted-foreground">{drug.quantity}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeDrug(drug.id)}
                  className="text-foreground hover:text-foreground size-9"
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSubmit} className="w-full md:w-auto" disabled={selectedDrugs.length === 0}>
          Submit Eligibility Check
        </Button>
      </div>
    </div>
  );
}
